// src/app/features/auth/service/idle-timer.service.ts
import { Injectable, NgZone } from '@angular/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { AuthService } from '@/features/auth/auth.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class IdleTimerService {
  // tempos (ajuste depois p/ 1h e 5min)
  private INACTIVITY_LIMIT_MS = 60 * 60 * 1000; // 1 hora
  private WARNING_WINDOW_MS   = 5 * 60 * 1000;  // últimos 5 min

  private activity$: Subscription | null = null;
  private idleTimer: any;
  private warnTimer: any;
  private tick: any;

  // 🔔 contador observado pelo template do toast
  public countdown$ = new BehaviorSubject<number | null>(null);

  constructor(
    private zone: NgZone,
    private msg: MessageService,
    private auth: AuthService
  ) {}

  start(): void {
    this.stop();
    const events$ = merge(
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'keydown'),
      fromEvent(window, 'click'),
      fromEvent(window, 'scroll'),
      fromEvent(window, 'touchstart')
    ).pipe(debounceTime(300));

    this.activity$ = events$.subscribe(() => this.resetTimers());
    this.resetTimers();
  }

  stop(): void {
    this.activity$?.unsubscribe();
    this.activity$ = null;
    clearTimeout(this.idleTimer);
    clearTimeout(this.warnTimer);
    clearInterval(this.tick);
    this.countdown$.next(null);
    this.msg.clear('idle');
  }

  private resetTimers(): void {
    clearTimeout(this.idleTimer);
    clearTimeout(this.warnTimer);
    clearInterval(this.tick);
    this.countdown$.next(null);
    this.msg.clear('idle');

    const warnAt = this.INACTIVITY_LIMIT_MS - this.WARNING_WINDOW_MS;
    this.warnTimer = setTimeout(() => this.showWarning(), warnAt);
    this.idleTimer = setTimeout(() => this.doLogout(), this.INACTIVITY_LIMIT_MS);
  }

  private showWarning(): void {
    const totalSecs = Math.floor(this.WARNING_WINDOW_MS / 1000);
    this.zone.run(() => {
      this.countdown$.next(totalSecs);
      // 👇 adiciona UMA mensagem (fica estática; conteúdo vem do observable)
      this.msg.add({
        key: 'idle',
        sticky: true,
        severity: 'warn',
        summary: 'Sessão inativa',
        detail: '' // conteúdo renderizado pelo template
      });
    });

    this.tick = setInterval(() => {
      this.zone.run(() => {
        const current = this.countdown$.value ?? 0;
        const next = Math.max(0, current - 1);
        this.countdown$.next(next);
        if (next <= 0) {
          clearInterval(this.tick);
          this.msg.clear('idle');
        }
      });
    }, 1000);
  }

  private async doLogout(): Promise<void> {
    this.stop();
    await this.auth.logout(); // 🔥 limpa storages, SW, caches e força reload com cache-buster
  }
}
